import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { useParams } from 'react-router-dom';
import { api, type ScheduledIntent, type SpaceSummary } from '../api';

// ── Helpers ──────────────────────────────────────────────────────

function fmtHMS(sec: number): [string, string, string] {
  const s = Math.max(0, Math.floor(Math.abs(sec)));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return [h, m, ss].map((v) => String(v).padStart(2, '0')) as [string, string, string];
}

function fmtNum(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

// ── Status helpers ───────────────────────────────────────────────

const STATUS_LABELS: Record<number, string> = {
  0: 'QUEUED',
  1: 'SENT',
  2: 'SUCCEEDED',
  3: 'FAILED',
  4: 'TIMEOUT',
  5: 'PREEMPTED',
  6: 'MISSED',
  7: 'CANCELLED',
};

function statusClass(status: number): string {
  switch (status) {
    case 2: return 'passed';
    case 3:
    case 4:
    case 6: return 'failed';
    case 5:
    case 7: return 'cancelled';
    default: return '';
  }
}

function isTerminal(status: number): boolean {
  return status >= 2;
}

// ── Sub-components ───────────────────────────────────────────────

function MissionTimer({
  elapsed,
  spaceTitle,
  t0Source,
}: {
  elapsed: number;
  spaceTitle: string;
  t0Source: 'operator' | 'connect_time' | 'none';
}): ReactElement {
  const sign = elapsed >= 0 ? 'T+' : 'T-';
  const [H, M, S] = fmtHMS(elapsed);
  const sourceLabel =
    t0Source === 'operator'
      ? 'ARMED T0'
      : t0Source === 'connect_time'
        ? 'SESSION T0'
        : 'NO T0';
  return (
    <div className="hud-timer">
      <div className="hud-timer-mission">{spaceTitle.toUpperCase()}</div>
      <div className="hud-timer-clock">
        <span className="hud-timer-sign">{sign}</span>
        <span className="hud-timer-val">{H}</span>
        <span className="hud-timer-sep">:</span>
        <span className="hud-timer-val">{M}</span>
        <span className="hud-timer-sep">:</span>
        <span className="hud-timer-val">{S}</span>
      </div>
      <div className="hud-timer-units">
        <div>HOURS</div>
        <div>MIN</div>
        <div>SEC</div>
      </div>
      <div className="hud-timer-foot">
        <span>
          <span className="hud-timer-dot" />
          {sourceLabel}
        </span>
        <span>T0 ANCHOR</span>
      </div>
    </div>
  );
}

function CircularGauge({
  label,
  value,
  unit,
  pct,
}: {
  label: string;
  value: number;
  unit: string;
  pct: number;
}): ReactElement {
  return (
    <div
      className="hud-gauge"
      style={
        {
          '--pct': `${pct}%`,
          '--a': `${pct * 3.6}deg`,
        } as React.CSSProperties
      }
    >
      <div className="hud-gauge-ring" />
      <div className="hud-gauge-tick" />
      <div className="hud-gauge-label">{label}</div>
      <div className="hud-gauge-num">{fmtNum(value)}</div>
      <div className="hud-gauge-unit">{unit}</div>
    </div>
  );
}

function TelemetryGauges({
  total,
  queued,
  completed,
}: {
  total: number;
  queued: number;
  completed: number;
}): ReactElement {
  const queuedPct = total > 0 ? (queued / total) * 100 : 0;
  const completedPct = total > 0 ? (completed / total) * 100 : 0;
  return (
    <div className="hud-telemetry">
      <CircularGauge label="QUEUED" value={queued} unit="INTENTS" pct={queuedPct} />
      <CircularGauge label="COMPLETE" value={completed} unit="INTENTS" pct={completedPct} />
    </div>
  );
}

// Extended intent with precomputed arc position
interface AnnotatedIntent extends ScheduledIntent {
  _p: number;
}

function ProgressArc({
  progress,
  elapsed,
  intents,
  arcWrapRef,
}: {
  progress: number;
  elapsed: number;
  intents: AnnotatedIntent[];
  arcWrapRef: React.RefObject<HTMLDivElement | null>;
}): ReactElement {
  const arcBaseRef = useRef<SVGPathElement>(null);
  const [milestonePositions, setMilestonePositions] = useState<Array<{ x: number; y: number }>>([]);
  const [rocketPos, setRocketPos] = useState<{ x: number; y: number; ang: number }>({
    x: 0,
    y: 0,
    ang: 0,
  });

  const pointOnArcPct = useCallback(
    (pct: number): { x: number; y: number } => {
      const path = arcBaseRef.current;
      if (!path) return { x: 0, y: 0 };
      const total = path.getTotalLength();
      const clamped = Math.max(0, Math.min(1, pct));
      const pt = path.getPointAtLength(total * clamped);
      return { x: pt.x, y: pt.y };
    },
    [],
  );

  const toPixel = useCallback(
    (p: { x: number; y: number }): { x: number; y: number } => {
      const wrap = arcWrapRef.current;
      if (!wrap) return { x: 0, y: 0 };
      const rect = wrap.getBoundingClientRect();
      return { x: (p.x / 1000) * rect.width, y: (p.y / 200) * rect.height };
    },
    [arcWrapRef],
  );

  // Layout milestones when intents or container size changes
  const layoutMilestones = useCallback(() => {
    setMilestonePositions(intents.map((m) => toPixel(pointOnArcPct(m._p))));
  }, [intents, pointOnArcPct, toPixel]);

  useEffect(() => {
    layoutMilestones();
    window.addEventListener('resize', layoutMilestones);
    return () => window.removeEventListener('resize', layoutMilestones);
  }, [layoutMilestones]);

  // Rocket position
  useEffect(() => {
    const rp = pointOnArcPct(progress);
    const ahead = pointOnArcPct(Math.min(1, progress + 0.005));
    const behind = pointOnArcPct(Math.max(0, progress - 0.005));
    const ang = (Math.atan2(ahead.y - behind.y, ahead.x - behind.x) * 180) / Math.PI + 90;
    const px = toPixel(rp);
    setRocketPos({ x: px.x, y: px.y, ang });
  }, [progress, pointOnArcPct, toPixel]);

  const [H, M, S] = fmtHMS(elapsed);
  const sign = elapsed >= 0 ? 'T+' : 'T-';

  return (
    <div className="hud-arc-wrap" ref={arcWrapRef}>
      <svg viewBox="0 0 1000 200" preserveAspectRatio="none" className="hud-arc-svg">
        <defs>
          <linearGradient id="arcHot" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#ff6a3d" />
            <stop offset="55%" stopColor="#ffb26a" />
            <stop offset="100%" stopColor="#ffe1c2" />
          </linearGradient>
          <filter id="glow" x="-20%" y="-40%" width="140%" height="180%">
            <feGaussianBlur stdDeviation="2.2" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {/* base arc */}
        <path
          ref={arcBaseRef}
          d="M 0 191 Q 500 -13 1000 191"
          fill="none"
          stroke="rgba(244,236,220,0.28)"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeDasharray="3 5"
        />
        {/* progress arc */}
        <path
          d="M 0 191 Q 500 -13 1000 191"
          fill="none"
          stroke="url(#arcHot)"
          strokeWidth="2.4"
          strokeLinecap="round"
          filter="url(#glow)"
          pathLength="1"
          strokeDasharray="1"
          strokeDashoffset={1 - Math.max(0, Math.min(1, progress))}
        />
      </svg>

      {/* Milestones */}
      {intents.map((intent, i) => {
        const pos = milestonePositions[i];
        if (!pos) return null;
        const passed = isTerminal(intent.status);
        const active = !passed && Math.abs(progress - intent._p) < 0.02;
        const cls = ['hud-milestone', statusClass(intent.status), active ? 'active' : '']
          .filter(Boolean)
          .join(' ');
        return (
          <div key={intent.id} className={cls} style={{ left: pos.x, top: pos.y }}>
            <div className="hud-milestone-node" />
            <div className="hud-milestone-lbl">
              <span className="hud-milestone-word">{intent.intentType}</span>
              <span
                className="hud-milestone-word"
                style={{ fontSize: '0.6rem', opacity: 0.7 }}
              >
                {STATUS_LABELS[intent.status]}
              </span>
            </div>
          </div>
        );
      })}

      {/* Rocket marker */}
      <div
        className="hud-rocket"
        style={{
          left: rocketPos.x,
          top: rocketPos.y,
          transform: `translate(-50%,-50%) rotate(${rocketPos.ang}deg)`,
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 2 L15 10 L15 18 L12 22 L9 18 L9 10 Z"
            fill="#fff"
            stroke="#ff6a3d"
            strokeWidth="1"
          />
          <path d="M9 14 L6 18 L9 17 Z M15 14 L18 18 L15 17 Z" fill="#ffb26a" />
          <circle cx="12" cy="10" r="1.5" fill="#ff6a3d" />
        </svg>
      </div>

      {/* Center readout */}
      <div className="hud-arc-readout">
        <div className="hud-arc-big">
          <span className="hud-arc-sign">{sign}</span>
          <span className="hud-arc-time">
            {H}:{M}:{S}
          </span>
        </div>
      </div>
    </div>
  );
}

function Instruments({ intents }: { intents: AnnotatedIntent[] }): ReactElement {
  const statusCounts: Record<string, number> = {};
  for (const i of intents) {
    const label = STATUS_LABELS[i.status] ?? `CODE ${i.status}`;
    statusCounts[label] = (statusCounts[label] ?? 0) + 1;
  }
  const entries = Object.entries(statusCounts).sort((a, b) => b[1] - a[1]);

  return (
    <div className="hud-instruments">
      <div className="hud-inst attitude">
        <div className="hud-inst-caption">INTENTS</div>
        <div className="hud-att-mark" />
        <div
          className="hud-inst-foot"
          style={{ flexDirection: 'column', gap: 2 }}
        >
          {entries.slice(0, 4).map(([label, count]) => (
            <span key={label}>
              {label}: {count}
            </span>
          ))}
          {entries.length === 0 && <span>NO DATA</span>}
        </div>
      </div>

      <div className="hud-inst earth">
        <div className="hud-inst-caption">T0 SOURCE</div>
        <div className="hud-globe">
          <div className="hud-globe-continents" />
        </div>
        <div className="hud-globe-grid" />
        <div className="hud-globe-marker" />
        <div className="hud-inst-foot">
          <span>
            {intents.length > 0
              ? (intents[0]?.t0Source ?? '').toUpperCase()
              : '—'}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Main Simulation Page ─────────────────────────────────────────

type PageState = 'loading' | 'no_t0' | 'empty' | 'active' | 'error';

export function Simulation(): ReactElement {
  const { id: spaceId } = useParams<{ id: string }>();
  const [space, setSpace] = useState<SpaceSummary | null>(null);
  const [intents, setIntents] = useState<ScheduledIntent[]>([]);
  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const arcWrapRef = useRef<HTMLDivElement>(null);

  // ── Fetch space + intents ────────────────────────────────────

  const fetchData = useCallback(async () => {
    if (!spaceId) return;
    try {
      const [s, ints] = await Promise.all([
        api.getSpace(spaceId),
        api.listScheduledIntents(spaceId),
      ]);
      setSpace(s);
      setIntents(ints);
      setErrorMsg(null);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to fetch data');
      setPageState('error');
    }
  }, [spaceId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Wall-clock polling ───────────────────────────────────────

  useEffect(() => {
    const tick = setInterval(() => setNowMs(Date.now()), 1000);
    const poll = setInterval(() => fetchData(), 5000);
    return () => {
      clearInterval(tick);
      clearInterval(poll);
    };
  }, [fetchData]);

  // ── Resolve T0 ───────────────────────────────────────────────

  const t0Info = useMemo(() => {
    if (!space) return { t0Ms: null, source: 'none' as const };
    if (space.scheduledT0Armed && space.scheduledT0At) {
      return {
        t0Ms: new Date(space.scheduledT0At).getTime(),
        source: 'operator' as const,
      };
    }
    if (space.currentSessionConnectedAt) {
      return {
        t0Ms: new Date(space.currentSessionConnectedAt).getTime(),
        source: 'connect_time' as const,
      };
    }
    return { t0Ms: null, source: 'none' as const };
  }, [space]);

  // ── Derive page state ────────────────────────────────────────

  useEffect(() => {
    if (errorMsg) {
      setPageState('error');
      return;
    }
    if (!space) {
      setPageState('loading');
      return;
    }
    if (!t0Info.t0Ms) {
      setPageState('no_t0');
      return;
    }
    // T0 exists — show HUD. "empty" is now a soft sub-state (no
    // milestones) rather than blocking the timer display.
    setPageState(intents.length === 0 ? 'empty' : 'active');
  }, [space, intents, t0Info, errorMsg]);

  // ── Compute positions and progress ───────────────────────────

  const { annotatedIntents, progress, elapsed } = useMemo(() => {
    if (!t0Info.t0Ms) {
      return { annotatedIntents: [], progress: 0, elapsed: 0 };
    }

    const elapsedSec = (nowMs - t0Info.t0Ms) / 1000;

    if (intents.length === 0) {
      // T0 exists but no intents yet — timer still counts down, arc empty
      return { annotatedIntents: [], progress: 0, elapsed: elapsedSec };
    }

    const latest = Math.max(...intents.map((i) => i.fireAtUnixMs));
    // At least 60s span so the arc isn't squeezed
    const span = Math.max(latest - t0Info.t0Ms, 60_000);

    const annotated: AnnotatedIntent[] = intents.map((i) => ({
      ...i,
      _p: (i.fireAtUnixMs - t0Info.t0Ms!) / span,
    }));

    const prog = Math.max(0, Math.min(1, (nowMs - t0Info.t0Ms) / span));

    return { annotatedIntents: annotated, progress: prog, elapsed: elapsedSec };
  }, [intents, t0Info, nowMs]);

  // ── Gauge values ─────────────────────────────────────────────

  const gaugeValues = useMemo(() => {
    const total = intents.length;
    const queued = intents.filter((i) => !isTerminal(i.status)).length;
    const completed = intents.filter((i) => i.status === 2).length;
    return { total, queued, completed };
  }, [intents]);

  // ── Phase label ──────────────────────────────────────────────

  const phaseLabel = useMemo(() => {
    switch (pageState) {
      case 'loading': return 'LOADING…';
      case 'no_t0': return 'NO T0 ANCHOR';
      case 'error': return 'ERROR';
      case 'empty':
      case 'active': break;
    }
    if (elapsed < 0) return `COUNTDOWN · T-${fmtHMS(elapsed).join(':')}`;
    const nextIntent = intents
      .filter((i) => !isTerminal(i.status))
      .sort((a, b) => a.fireAtUnixMs - b.fireAtUnixMs)[0];
    if (nextIntent) {
      const remainingMs = nextIntent.fireAtUnixMs - nowMs;
      const remainingSec = Math.max(0, Math.floor(remainingMs / 1000));
      const [M, S] = [Math.floor(remainingSec / 60), remainingSec % 60].map((v) =>
        String(v).padStart(2, '0'),
      );
      return `NEXT: ${nextIntent.intentType} · ${M}:${S}`;
    }
    if (intents.length === 0) return 'WAITING FOR INTENTS';
    return 'ALL INTENTS COMPLETE';
  }, [pageState, elapsed, intents, nowMs]);

  // ── Render ───────────────────────────────────────────────────

  return (
    <div className="hud-stage">
      {/* Decorative layers */}
      <div className="hud-stars" />
      <div className="hud-horizon-line" />
      <div className="hud-glint" />
      <div className="hud-vignette" />
      <div className="hud-grain" />

      {/* Corner brackets */}
      <div className="hud-corner tl" />
      <div className="hud-corner tr" />
      <div className="hud-corner bl" />
      <div className="hud-corner br" />

      {/* Status badge */}
      <div className="hud-live-badge">
        <span
          className={`hud-live-badge-dot${pageState === 'active' ? '' : ' hud-live-badge-dot--dim'}`}
        />
        {pageState === 'active' ? 'LIVE' : pageState.toUpperCase().replace('_', ' ')}
      </div>

      {/* Phase label */}
      <div className="hud-phase-label">{phaseLabel}</div>

      {/* Mission timer (top-right) */}
      <MissionTimer
        elapsed={elapsed}
        spaceTitle={space?.title ?? '—'}
        t0Source={t0Info.source}
      />

      {/* Bottom HUD bar */}
      <div className="hud-bottom">
        <TelemetryGauges {...gaugeValues} />
        <ProgressArc
          progress={progress}
          elapsed={elapsed}
          intents={annotatedIntents}
          arcWrapRef={arcWrapRef}
        />
        <Instruments intents={annotatedIntents} />
      </div>

      {/* Error overlay */}
      {pageState === 'error' && (
        <div className="hud-error-overlay">
          <div className="hud-error-msg">{errorMsg}</div>
          <button
            className="hud-error-retry"
            onClick={() => {
              setErrorMsg(null);
              fetchData();
            }}
          >
            RETRY
          </button>
        </div>
      )}

      {/* No-T0 overlay — full screen, HUD hidden behind */}
      {pageState === 'no_t0' && (
        <div className="hud-empty-hint">
          Arm T0 in Space Settings or connect a UE client
        </div>
      )}

      {/* Empty hint — subtle, HUD still visible behind */}
      {pageState === 'empty' && (
        <div className="hud-empty-hint">
          No scheduled intents yet. Submit deferred intents via MCP.
        </div>
      )}
    </div>
  );
}
